import { isCuttingTool, useDesignerStore } from "@/state/designerStore";

/**
 * The line the user has drawn, painted over the viewport.
 *
 * Points are held in normalised device coordinates and the SVG uses a matching
 * -1..1 viewBox with `preserveAspectRatio="none"`, so the line stays glued to
 * the same place on the model when the window is resized.
 *
 * The dashed tails are not decoration: the cut extends the drawn line well past
 * both ends before sweeping it, which is what makes every cut a clean break
 * rather than a partial slot. Showing that is the difference between a tool
 * that surprises people and one that does not.
 */
export function CutOverlay(): React.ReactElement | null {
  const tool = useDesignerStore((s) => s.tool);
  const points = useDesignerStore((s) => s.cutPoints);
  const angleDegrees = useDesignerStore((s) => s.cutAngleDegrees);

  if (!isCuttingTool(tool) || points.length === 0) return null;

  const path = points.map(([x, y]) => `${x.toFixed(4)},${(-y).toFixed(4)}`).join(" ");
  const first = points[0] as [number, number];
  const last = points[points.length - 1] as [number, number];
  const startTail = points.length > 1 ? tail(points[1] as [number, number], first) : null;
  const endTail =
    points.length > 1 ? tail(points[points.length - 2] as [number, number], last) : null;

  const midpoint: [number, number] | null = points.length > 1
    ? [(first[0] + last[0]) / 2, (first[1] + last[1]) / 2]
    : null;

  return (
    <>
      <svg
        className="cut-overlay"
        viewBox="-1 -1 2 2"
        preserveAspectRatio="none"
        aria-hidden
        focusable="false"
      >
        {startTail ? (
          <line
            x1={first[0]}
            y1={-first[1]}
            x2={startTail[0]}
            y2={-startTail[1]}
            className="cut-overlay__tail"
          />
        ) : null}
        {endTail ? (
          <line
            x1={last[0]}
            y1={-last[1]}
            x2={endTail[0]}
            y2={-endTail[1]}
            className="cut-overlay__tail"
          />
        ) : null}
        {points.length > 1 ? <polyline points={path} className="cut-overlay__line" /> : null}
      </svg>
      {tool === "angle" && midpoint && angleDegrees !== null ? (
        <span
          className="cut-overlay__angle"
          style={{
            left: `${(midpoint[0] + 1) * 50}%`,
            top: `${(1 - midpoint[1]) * 50}%`,
          }}
          aria-hidden
        >
          {angleDegrees}°
        </span>
      ) : null}
    </>
  );
}

/** Project the end of the line out past the edge of the viewport. */
function tail(inner: [number, number], end: [number, number]): [number, number] {
  const dx = end[0] - inner[0];
  const dy = end[1] - inner[1];
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return end;
  return [end[0] + (dx / length) * 4, end[1] + (dy / length) * 4];
}
